import React from 'react';
import { Bot } from 'lucide-react';
import { Card } from '@/components/ui/card';
import {
  idKey,
  assistantLineKey,
  assistantAddonLineKey,
  assistantSuggestedLineKey,
} from '@/lib/assistant/assistantLineKeys';
import { AssistantAddonCard } from '@/components/assistant/AssistantAddonCard';
import { AssistantProductCard } from '@/components/assistant/AssistantProductCard';
import { AssistantProductScrollList } from '@/components/assistant/AssistantProductScrollList';
import { AssistantMessageOptionButtons } from '@/components/assistant/AssistantMessageOptionButtons';
import { AssistantOrderSummaryCard } from '@/components/assistant/AssistantOrderSummaryCard';

export function AssistantMessageList({
  messages,
  messagesEndRef,
  messagesBottomPad,
  primaryColor,
  business,
  benefitsProgressSnapshot,
  getAssistantLineQty,
  bumpAssistantLineQty,
  getCartQtyForProductId,
  onPostAddonAdd,
  onCatalogProductAdd,
  onSuggestedProductAdd,
  onOrderSummaryLineQty,
  onOrderSummaryRemoveLine,
}) {
  return (
    <div className={`max-w-4xl mx-auto px-4 py-6 ${messagesBottomPad}`}>
      <div className="space-y-6">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex gap-3 ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {message.type === 'bot' ? (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <Bot className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
            ) : null}

            <div className="max-w-[80%] space-y-3">
              {message.text ? (
                <Card
                  className={`p-4 ${message.type === 'user' ? 'text-white' : 'bg-card'}`}
                  style={message.type === 'user' ? { backgroundColor: primaryColor } : {}}
                >
                  <p className="whitespace-pre-line text-sm leading-relaxed">{message.text}</p>
                </Card>
              ) : null}

              <AssistantMessageOptionButtons
                options={message.preProductOptions}
                testIdPrefix="pre-option"
                primaryColor={primaryColor}
              />

              {message.postAddAddonCards?.length > 0 ? (
                <div className="grid grid-cols-1 gap-3">
                  {message.postAddAddonCards.map((row) => {
                    const lineKey = assistantAddonLineKey(index, row.productId, row.addonIndex);
                    const lineQty = getAssistantLineQty(lineKey);
                    return (
                      <AssistantAddonCard
                        key={`addon-card-${idKey(row.productId)}-${row.addonIndex}`}
                        row={row}
                        lineQty={lineQty}
                        primaryColor={primaryColor}
                        onBumpQty={(delta) => bumpAssistantLineQty(lineKey, delta)}
                        testId={`post-add-addon-${row.addonIndex}`}
                        onAdd={() => onPostAddonAdd(row, lineQty)}
                      />
                    );
                  })}
                </div>
              ) : null}

              {message.products?.length > 0 ? (
                message.productsScrollViewport ? (
                  <AssistantProductScrollList
                    products={message.products}
                    messageIndex={index}
                    visibleCount={message.productsScrollVisibleCount ?? 3}
                    primaryColor={primaryColor}
                    business={business}
                    benefitsProgressSnapshot={benefitsProgressSnapshot}
                    getAssistantLineQty={getAssistantLineQty}
                    bumpAssistantLineQty={bumpAssistantLineQty}
                    getCartQtyForProductId={getCartQtyForProductId}
                    onCatalogProductAdd={onCatalogProductAdd}
                    message={message}
                  />
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {message.products.map((product, prodIndex) => {
                      const isWelcomeClubReward = product.assistantWelcomeKind === 'club_reward';
                      const maxRewardQty = isWelcomeClubReward
                        ? Math.max(
                            0,
                            Math.min(99, Number(benefitsProgressSnapshot?.available_rewards) || 0)
                          )
                        : 99;
                      const isConfirmation = message.productCardMode === 'confirmation';
                      const lineKey = assistantLineKey(index, product.id);
                      const lineQty = getAssistantLineQty(lineKey);
                      return (
                        <AssistantProductCard
                          key={prodIndex}
                          product={product}
                          primaryColor={primaryColor}
                          lineQty={lineQty}
                          inCartQty={getCartQtyForProductId(product.id)}
                          onBumpQty={(delta) => bumpAssistantLineQty(lineKey, delta)}
                          isConfirmation={isConfirmation}
                          isWelcomeClubReward={isWelcomeClubReward}
                          maxRewardQty={maxRewardQty}
                          business={business}
                          testId={`add-to-cart-${product.id || prodIndex}`}
                          addLabel={isWelcomeClubReward ? 'Canjear' : 'Agregar'}
                          onAdd={() =>
                            onCatalogProductAdd({
                              product,
                              message,
                              lineQty,
                              isWelcomeClubReward,
                            })
                          }
                        />
                      );
                    })}
                  </div>
                )
              ) : null}

              {message.textAfterProducts ? (
                <Card className="p-4 bg-card">
                  <p className="whitespace-pre-line text-sm leading-relaxed">
                    {message.textAfterProducts}
                  </p>
                </Card>
              ) : null}

              {message.suggestedProducts?.length > 0 ? (
                <div className="grid grid-cols-1 gap-3">
                  {message.suggestedProducts.map((product, prodIndex) => {
                    const sugKey = assistantSuggestedLineKey(index, prodIndex, product.id);
                    const lineQty = getAssistantLineQty(sugKey);
                    return (
                      <AssistantProductCard
                        key={`sug-${product.id || prodIndex}`}
                        product={product}
                        primaryColor={primaryColor}
                        lineQty={lineQty}
                        inCartQty={getCartQtyForProductId(product.id)}
                        variant="suggested"
                        business={business}
                        testId={`contextual-add-${product.id || prodIndex}`}
                        onBumpQty={(delta) => bumpAssistantLineQty(sugKey, delta)}
                        onAdd={() =>
                          onSuggestedProductAdd({
                            product,
                            message,
                            lineQty,
                          })
                        }
                      />
                    );
                  })}
                </div>
              ) : null}

              {message.orderSummary ? (
                <AssistantOrderSummaryCard
                  preview={message.orderSummary}
                  primaryColor={primaryColor}
                  editable={message.orderSummaryEditable === true}
                  onLineQtyChange={onOrderSummaryLineQty}
                  onRemoveLine={onOrderSummaryRemoveLine}
                />
              ) : null}

              <AssistantMessageOptionButtons
                options={message.options}
                testIdPrefix="option"
                primaryColor={primaryColor}
              />
            </div>

            {message.type === 'user' ? (
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                <span className="text-lg">👤</span>
              </div>
            ) : null}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
}
